/**
 * Pool Reports Distribution Page
 *
 * Displays pool distribution data by owner with date filtering,
 * breakdown toggles, and CSV export functionality
 * UI matches Angular implementation exactly
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ExternalLink } from 'lucide-react';
import { Button, Toggle, Skeleton } from '@/components/ui';
import { PoolReportTableCard } from '@/components/poolReports/PoolReportTableCard';
import {
  poolReportOwnerLabelClass,
  poolReportOwnerLinkClass,
  poolReportSelectedCellClass,
} from '@/components/poolReports/poolReportTableHighlight';
import {
  POOL_REPORT_OWNER_COL_HEADER,
  poolReportTheadMutedClass,
  poolReportTheadStickyClass,
  poolReportThClass,
} from '@/components/poolReports/poolReportTableChrome';
import { usePoolReportsStore } from '@/store';
import { useDistributionDates, useDistributions } from '@/hooks';
import { CSVService } from '@/utils/csvExport';
import { formatNumber } from '@/utils/formatters';
import type { Distribution } from '@/types/poolReports.types';

interface HeadingItem {
  type: string;
  value: string;
  index: number;
  isBasisYear: boolean | null;
  key: string;
}

export function PoolReportsDistributionPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { reportType, selectedReport } = usePoolReportsStore();
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [headings, setHeadings] = useState<HeadingItem[]>([]);
  const [displayData, setDisplayData] = useState<Distribution[]>([]);

  // Toggle states
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showPostYear, setShowPostYear] = useState(false);
  const [highlightedRowKey, setHighlightedRowKey] = useState<string | null>(null);

  const fileId = searchParams.get('fileId');
  const poolId = searchParams.get('poolId');
  const dateParam = searchParams.get('date');

  // Fetch distribution dates
  const { data: reportDates, isLoading: datesLoading } = useDistributionDates(
    fileId ? parseInt(fileId, 10) : 0,
    !!fileId
  );

  // Fetch distributions for selected date
  const { data: distributions, isLoading: distributionsLoading } = useDistributions(
    fileId ? parseInt(fileId, 10) : 0,
    selectedDate,
    !!fileId && !!selectedDate
  );

  // Set initial selected date from URL or first date
  useEffect(() => {
    if (!reportDates || reportDates.length === 0) return;

    if (dateParam && reportDates.some((d) => d.DistributionDateStr === dateParam)) {
      setSelectedDate(dateParam);
    } else {
      const firstDate = reportDates[0].DistributionDateStr;
      setSelectedDate(firstDate);
      setSearchParams({
        poolId: poolId || '',
        fileId: fileId || '',
        date: firstDate,
      });
    }
  }, [reportDates, dateParam, fileId, poolId, setSearchParams]);

  // Process headings and filter data when distributions change
  useEffect(() => {
    if (!distributions || distributions.length === 0) {
      setHeadings([]);
      setDisplayData([]);
      return;
    }

    // Get basis year from first row
    const basisYear = distributions[0].BasisYear;

    // Filter out excluded fields
    const excludeFields = ['basisyear', 'ownerid', 'vesselid'];
    const filtered = distributions.map((distribution: Distribution) => {
      const filtered: Distribution = {};
      Object.keys(distribution).forEach((key) => {
        if (!excludeFields.includes(key.toLowerCase())) {
          filtered[key] = distribution[key];
        }
      });
      return filtered;
    });

    // Build heading structure
    if (filtered.length > 0) {
      const headingItems: HeadingItem[] = Object.keys(filtered[0]).map((key, index) => {
        const parts = key.split('|');
        const displayValue =
          parts[0] === 'OwnerName'
            ? POOL_REPORT_OWNER_COL_HEADER
            : parts.length > 1
              ? parts[1]
              : parts[0];
        const type = parts.length > 1 ? parts[0] : '';

        let isBasisYear: boolean | null = null;

        // Determine if this column is for the basis year
        if (parts.length > 1 && ['current', 'post', 'breakdown'].includes(parts[0])) {
          const lastPart = parts[1].split(' ').pop() || '';
          const year = lastPart.replace(/[^0-9]/g, '');
          isBasisYear = Number(basisYear) === Number(year);
        }

        if (key.toLowerCase().indexOf('previous years') > -1) {
          isBasisYear = false;
        }

        return {
          type,
          value: displayValue,
          index,
          isBasisYear,
          key,
        };
      });

      setHeadings(headingItems);
      setDisplayData(filtered);
    }
  }, [distributions]);

  // Handle date selection change
  const handleDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setSearchParams({
      poolId: poolId || '',
      fileId: fileId || '',
      date: newDate,
    });
  };

  // Handle showPostYear toggle - scroll to right when enabled
  const handlePostYearToggle = (value: boolean) => {
    setShowPostYear(value);

    if (value && tableWrapperRef.current) {
      setTimeout(() => {
        if (tableWrapperRef.current) {
          tableWrapperRef.current.scrollTo({
            behavior: 'smooth',
            left: tableWrapperRef.current.scrollWidth - tableWrapperRef.current.clientWidth,
          });
        }
      }, 50);
    }
  };

  const handleExportCSV = () => {
    if (displayData.length === 0 || headings.length === 0) return;

    // Filter headings based on toggles for export
    const visibleHeadings = headings.filter(({ type }) => {
      if (!showBreakdown && type === 'breakdown') return false;
      if (!showPostYear && type === 'post') return false;
      return true;
    });

    const reportRows = displayData.map((item: Distribution) =>
      visibleHeadings.map((head, index) => {
        const value = item[head.key];
        if (value === null || value === undefined) return '';
        if (index > 0 && typeof value === 'number') return formatNumber(value, 0, 0);
        return String(value);
      })
    );

    const fileName = `${selectedReport?.description || 'report'}-distribution-report`;
    CSVService.convert(
      reportRows,
      visibleHeadings.map(({ value }) => value)
    ).download(fileName);
  };

  const handleOwnerClick = (index: number) => {
    const ownerData = distributions?.[index];
    if (!ownerData?.OwnerID) return; // Don't navigate for total row

    const route = `/reports/${reportType}/distribution/owner/${ownerData.OwnerID}`;
    navigate(
      `${route}?poolId=${poolId}&fileId=${fileId}&ownerName=${encodeURIComponent(ownerData.OwnerName || '')}&date=${selectedDate}`
    );
  };

  const isTotal = (index: number) => {
    const ownerData = distributions?.[index];
    return !ownerData?.OwnerID;
  };

  const isLoading = datesLoading || distributionsLoading;

  // Build table class names for CSS-based column visibility (matches Angular)
  // Uses distribution-table class to scope the CSS rules that hide .post and .breakdown columns
  const tableClassName =
    `distribution-table report-table ${showBreakdown ? 'show-breakdown' : ''} ${
      showPostYear ? 'show-post-year' : ''
    }`.trim();

  return (
    <div className="pool-reports-distribution-page space-y-4">
      <style>{`
        @media (min-width: 768px) {
          .pool-reports-distribution-page .distribution-table th:first-child,
          .pool-reports-distribution-page .distribution-table td:first-child {
            position: sticky;
            left: 0;
            min-width: 12rem;
            max-width: min(40vw, 24rem);
            border-right: solid 1px rgb(229 231 235);
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            vertical-align: top;
            box-shadow: 2px 0 6px rgba(15, 23, 42, 0.06);
            clip-path: inset(0px -10px 0px 0px);
            z-index: 9;
          }
          .dark .pool-reports-distribution-page .distribution-table th:first-child,
          .dark .pool-reports-distribution-page .distribution-table td:first-child {
            border-right: solid 1px rgb(51 65 85);
            box-shadow: 2px 0 8px rgba(0, 0, 0, 0.35);
          }
          .pool-reports-distribution-page .distribution-table thead th:first-child {
            z-index: 30;
            background-color: rgb(249 250 251 / 0.95);
          }
          .dark .pool-reports-distribution-page .distribution-table thead th:first-child {
            background-color: rgb(15 23 42 / 0.95);
          }
        }
        @media (max-width: 767px) {
          .pool-reports-distribution-page .distribution-table tbody td:first-child {
            position: static !important;
            left: auto !important;
            min-width: 11rem;
            max-width: none;
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            overflow: visible;
            vertical-align: top;
            font-size: 0.875rem;
            line-height: 1.4;
            padding: 0.5rem 0.65rem;
            border-right: 1px solid rgb(229 231 235);
            box-shadow: none !important;
            clip-path: none !important;
          }
          .pool-reports-distribution-page .distribution-table thead th:first-child {
            position: static !important;
            left: auto !important;
            min-width: 11rem;
            max-width: none;
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            overflow: visible;
            vertical-align: top;
            border-right: 1px solid rgb(229 231 235);
            box-shadow: none !important;
            clip-path: none !important;
          }
          .dark .pool-reports-distribution-page .distribution-table thead th:first-child,
          .dark .pool-reports-distribution-page .distribution-table tbody td:first-child {
            border-right-color: rgb(51 65 85);
          }
        }
      `}</style>
      <PoolReportTableCard
        eyebrow="Distributions"
        title="Distribution by owner"
        description="Choose the distribution date and optional column sets. Table scrolls horizontally when needed."
        toolbar={
          <>
            <select
              value={selectedDate}
              onChange={handleDateChange}
              disabled={isLoading || !reportDates || reportDates.length === 0}
              className="min-w-[10rem] max-w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500 dark:border-dark-600 dark:bg-dark-800 dark:text-white dark:focus:border-primary-400 sm:min-w-[180px] sm:px-3 sm:py-2"
            >
              <option value="" disabled>
                Distribution From
              </option>
              {reportDates?.map((date) => (
                <option key={date.DistributionDateStr} value={date.DistributionDateStr}>
                  {date.DisplayDate}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2 rounded-lg border border-gray-200/80 bg-white/90 px-2 py-1 dark:border-dark-600 dark:bg-dark-900/50">
                <Toggle
                  id="showBreakdown"
                  checked={showBreakdown}
                  onChange={setShowBreakdown}
                  disabled={isLoading}
                />
                <label
                  htmlFor="showBreakdown"
                  className="text-xs font-medium text-gray-700 dark:text-gray-300 sm:text-sm"
                >
                  Breakdown By Year
                </label>
              </div>
              {reportType === 'yearly' && (
                <div className="flex items-center gap-2 rounded-lg border border-gray-200/80 bg-white/90 px-2 py-1 dark:border-dark-600 dark:bg-dark-900/50">
                  <Toggle
                    id="showPostYear"
                    checked={showPostYear}
                    onChange={handlePostYearToggle}
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="showPostYear"
                    className="text-xs font-medium text-gray-700 dark:text-gray-300 sm:text-sm"
                  >
                    Post Year Distribution
                  </label>
                </div>
              )}
            </div>
          </>
        }
        endAdornment={
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCSV}
            leftIcon={<Download className="h-4 w-4" />}
            disabled={isLoading || displayData.length === 0}
            className="w-full rounded-full px-4 shadow-sm sm:w-auto"
          >
            Export to Excel
          </Button>
        }
      >
        <div ref={tableWrapperRef} className="report-table-shell">
          <table className={tableClassName}>
            {isLoading ? (
              <>
                {/* Loading skeleton */}
                <thead className={poolReportTheadMutedClass}>
                  <tr>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <th key={i} className={poolReportThClass}>
                        <Skeleton className="ml-auto h-4 w-20" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-900">
                  {Array.from({ length: 7 }).map((_, rowIdx) => (
                    <tr key={rowIdx} className="border-t border-gray-200 dark:border-dark-700">
                      {Array.from({ length: 12 }).map((_, colIdx) => (
                        <td key={colIdx} className="px-2.5 py-2 sm:px-3 sm:py-2.5">
                          <Skeleton className="h-6 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </>
            ) : (
              <>
                {/* Table Header - Render ALL headings, CSS controls visibility */}
                {headings.length > 0 && (
                  <thead className={poolReportTheadStickyClass}>
                    <tr>
                      {headings.map((heading) => (
                        <th
                          key={heading.key}
                          className={clsx(
                            poolReportThClass,
                            heading.type,
                            heading.isBasisYear === true
                              ? 'basis-year'
                              : heading.isBasisYear === false
                                ? 'non-basis-year'
                                : ''
                          )}
                        >
                          {heading.value}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}

                {/* Table Body - Render ALL columns, CSS controls visibility */}
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                  {displayData.map((row, rowIdx) => {
                    const isTotalRow = isTotal(rowIdx);
                    const ownerSource = distributions?.[rowIdx];
                    const rowKey = isTotalRow
                      ? '__total__'
                      : String(ownerSource?.OwnerID ?? `idx-${rowIdx}`);
                    const isHighlighted = !isTotalRow && highlightedRowKey === rowKey;
                    const hi = poolReportSelectedCellClass(isHighlighted);

                    return (
                      <tr
                        key={rowIdx}
                        onClick={() => {
                          if (isTotalRow) return;
                          setHighlightedRowKey((prev) => (prev === rowKey ? null : rowKey));
                        }}
                        className={
                          isTotalRow
                            ? undefined
                            : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-800'
                        }
                      >
                        {headings.map((heading, colIdx) => {
                          const value = row[heading.key];
                          const isOwnerColumn = colIdx === 0;

                          return (
                            <td
                              key={heading.key}
                              className={clsx(
                                'px-2.5 py-2 text-right text-sm sm:px-3 sm:py-2.5',
                                !isOwnerColumn && 'tabular-nums',
                                heading.type,
                                heading.isBasisYear === true
                                  ? 'basis-year'
                                  : heading.isBasisYear === false
                                    ? 'non-basis-year'
                                    : '',
                                isOwnerColumn &&
                                  (isTotalRow
                                    ? 'md:bg-gray-50 dark:md:bg-dark-800'
                                    : 'md:bg-white dark:md:bg-dark-900'),
                                hi
                              )}
                            >
                              {isOwnerColumn && value ? (
                                isTotalRow ? (
                                  <span className={poolReportOwnerLabelClass}>{value}</span>
                                ) : (
                                  <div className="flex w-full min-w-0 flex-row items-center justify-end gap-2">
                                    <button
                                      type="button"
                                      className={clsx(
                                        poolReportOwnerLabelClass,
                                        poolReportOwnerLinkClass,
                                        'min-w-0 flex-1 text-right'
                                      )}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOwnerClick(rowIdx);
                                      }}
                                    >
                                      {value}
                                    </button>
                                    <ExternalLink
                                      className="size-[1.125rem] shrink-0 text-gray-500 dark:text-gray-400"
                                      aria-hidden
                                    />
                                  </div>
                                )
                              ) : (
                                <span className="text-gray-900 dark:text-white">
                                  {typeof value === 'number'
                                    ? formatNumber(value, 0, 0)
                                    : value || ''}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </>
            )}
          </table>
        </div>
      </PoolReportTableCard>

      {/* No Data State */}
      {!isLoading && displayData.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No distribution data available</p>
        </div>
      )}
    </div>
  );
}
